import styled from "styled-components";
import { RACheckbox, RATextInput, RATypography } from "@/components/customs";
import { Row, Col, Tooltip, Grid } from "antd";
import { SvgCalendar, SvgClose, SvgDirection, SvgInfo, SvgLocation, SvgProfile } from "@/assets/icons";
import { RAInputSearchAirport, RAInputPassenger } from "@/components/atoms";
import { FLIGHT_REQUEST_OPTIONS } from "@/config/constants";
import { IconSwitchLeg } from "@/components/svgs";
import { useState, useRef, useEffect } from "react";
import { InputDepartureDate } from "./InputDepartureDate";
import { InputDepartureWindow } from "./InputDepartureWindow";
const { useBreakpoint } = Grid;

export function FormRequestFlightItem({
	type,
	title = "",
	switchOptions = false,
	onSwitchOptions,
	formValues = {},
	onChange,
	allowRemove,
	onRemoveLeg,
	legs = [],
	legIndex,
	error = {},
	allowSelectAirport = true,
	disabledFromDate,
	prevLegDate = null,
}) {
	const { from_airport, to_airport, passengers, departureWindow = false } = formValues;
	const screens = useBreakpoint();
	const [internalError, setInternalError] = useState({});
	const departureAirportRef = useRef(null);
	const arrivalAirportRef = useRef(null);

	useEffect(() => {
		if (process.env.NEXT_PUBLIC_ENV === "prod") return;
		if (!window.__airportSearchRefs) window.__airportSearchRefs = {};
		window.__airportSearchRefs[`departure_${legIndex}`] = departureAirportRef.current;
		window.__airportSearchRefs[`arrival_${legIndex}`] = arrivalAirportRef.current;
	}, [legIndex]);

	const getStatusDisabledDepartAirport = () => {
		let disabled = false;
		if (!allowSelectAirport || (type !== FLIGHT_REQUEST_OPTIONS.ONE_WAY && legIndex > 0)) {
			disabled = true;
		}
		return disabled;
	};

	const getStatusDisabledArrivalAirport = () => {
		let disabled = false;
		if (!allowSelectAirport || (type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP && legIndex > 0)) {
			disabled = true;
		}
		return disabled;
	};

	const _renderSelectDepartureDate = () => {
		if (!departureWindow) {
			return (
				<Row gutter={0}>
					<Col span={24}>
						<InputDepartureDate
							formValues={formValues}
							onChange={onChange}
							error={error}
							disabledFromDate={disabledFromDate}
							prevLegDate={prevLegDate}
							legIndex={legIndex}
						/>
					</Col>
				</Row>
			);
		}

		return (
			<InputDepartureWindow
				formValues={formValues}
				onChange={onChange}
				error={error}
				disabledFromDate={disabledFromDate}
				prevLegDate={prevLegDate}
			/>
		);
	};

	const _renderSwitchOptions = () => {
		if (switchOptions && screens.md) {
			return (
				<div style={{ backgroundColor: "orange" }}>
					<ButtonSwitchOptions onClick={() => onSwitchOptions?.()}>
						<IconSwitchLeg />
					</ButtonSwitchOptions>
				</div>
			);
		}
	};

	return (
		<WrapperFlightInfo>
			{!!title && (
				<Row justify="space-between" align="middle">
					<Col>
						<RATypography variant="label3">{title}</RATypography>
					</Col>
					{allowRemove && (
						<Col onClick={onRemoveLeg} style={{ cursor: "pointer" }}>
							<SvgClose />
						</Col>
					)}
				</Row>
			)}
			<Row gutter={[20, 20]} style={{ position: "relative" }}>
				<Col md={12} xs={24}>
					<RAInputSearchAirport
						ref={departureAirportRef}
						prefix={<SvgDirection color="#595959" />}
						placeholder="Select departure airport"
						disabled={getStatusDisabledDepartAirport()}
						value={from_airport}
						error={internalError?.from_airport || error?.from_airport || null}
						data-testid={`flightLeg_departureAirport_${legIndex}`}
						onSelect={(value) => {
							if (to_airport && to_airport._id && to_airport._id === value?._id) {
								setInternalError({
									from_airport: "Departure and arrival airports should be different",
								});
								return;
							} else {
								setInternalError({});
							}

							let nextLeg;
							if (type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP && legIndex === 0 && legs?.length >= 1) {
								nextLeg = { ...legs[1], to_airport: value };
							}
							onChange(
								{
									...formValues,
									from_airport: value,
								},
								nextLeg,
							);
						}}
					/>
				</Col>
				{_renderSwitchOptions()}
				<Col md={12} xs={24}>
					<RAInputSearchAirport
						ref={arrivalAirportRef}
						prefix={<SvgLocation color="#595959" />}
						placeholder="Select arrival airport"
						disabled={getStatusDisabledArrivalAirport()}
						value={to_airport}
						error={internalError?.to_airport || error?.to_airport || null}
						data-testid={`flightLeg_arrivalAirport_${legIndex}`}
						onSelect={(value) => {
							if (from_airport && from_airport._id && from_airport._id === value?._id) {
								setInternalError({
									to_airport: "Departure and arrival airports should be different",
								});
								return;
							} else {
								setInternalError({});
							}

							let nextLeg;
							if (type !== FLIGHT_REQUEST_OPTIONS.ONE_WAY && legIndex + 1 <= legs?.length - 1) {
								nextLeg = {
									...legs[legIndex + 1],
									from_airport: value,
								};
							}
							onChange(
								{
									...formValues,
									to_airport: value,
								},
								nextLeg,
							);
						}}
					/>
				</Col>
			</Row>
			<Row gutter={[20, 20]}>
				<Col md={12} xs={24}>
					{_renderSelectDepartureDate()}
				</Col>
				<Col md={12} xs={24}>
					{screens?.lg ? (
						<RATextInput
							prefix={<SvgProfile color="#595959" />}
							type="number"
							placeholder="Passengers"
							min={1}
							value={passengers}
							onChange={(val) =>
								onChange({
									...formValues,
									passengers: val,
								})
							}
							error={error?.passengers}
							data-testid={`flightLeg_passengers_${legIndex}`}
						/>
					) : (
						<RAInputPassenger
							style={{ width: "100%" }}
							size="large"
							value={passengers}
							min={1}
							onChange={(val) =>
								onChange({
									...formValues,
									passengers: val,
								})
							}
							data-testid={`flightLeg_passengers_${legIndex}`}
						/>
					)}
				</Col>
			</Row>
			<Row>
				<WrapperDepartureWindow>
					<RACheckbox
						prefix={<SvgCalendar color="#595959" />}
						title="Provide departure window"
						checked={departureWindow}
						onChange={() =>
							onChange({
								...formValues,
								departure_datetime: "",
								departure_window_datetime: "",
								departureWindow: !departureWindow,
							})
						}
					/>
					<Tooltip
						placement="bottomRight"
						title="Specify a range of preferred departure times to receive better aircraft options and potential cost savings."
					>
						<div style={{ marginBottom: -10 }}>
							<SvgInfo />
						</div>
					</Tooltip>
				</WrapperDepartureWindow>
			</Row>
		</WrapperFlightInfo>
	);
}

const WrapperFlightInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	margin-top: 30px;
`;
const WrapperDepartureWindow = styled.div`
	display: flex;
	align-items: center;
`;
const ButtonSwitchOptions = styled.div`
	margin: 0;
	position: absolute;
	left: 50%;
	top: 65%;
	-ms-transform: translate(-50%, -50%);
	transform: translate(-50%, -50%);
	z-index: 10;
	cursor: pointer;
`;
const WrapperIcon = styled.div`
	box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.06);
	display: flex;
	justify-content: center;
	align-items: center;
`;
